/*
  SuggestionComparisonDialog — spec §6 (table 9), §5 (source hierarchy)
  side-by-side comparison view opened when a transcript suggestion's
  destination field already has a clinician-verified value.

  Renders three tone-mapped panels — Patient onboarding, Existing
  assessment value, Transcript suggestion — with per-source labels,
  timestamps and (for transcript) speaker + evidence + assertion
  metadata. The clinician picks ONE of:
    • Keep existing value (no change).
    • Accept transcript value (overwrite + write to assessment).
    • Edit and accept (custom final value + overwrite).

  Every choice returns a promise the caller awaits so it can refresh
  the review queue.
*/
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ClipboardList, User, Mic, AlertTriangle, ScrollText, Flag, Play } from "lucide-react";
import ReportTranscriptionErrorDialog from "@/components/ReportTranscriptionErrorDialog";
import EvidenceDrawer from "@/components/EvidenceDrawer";

const SOURCE_STYLES = {
  onboarding: {
    icon: ClipboardList,
    tone: "border-indigo-300 bg-indigo-50",
    heading: "text-indigo-900",
  },
  existing: {
    icon: User,
    tone: "border-emerald-300 bg-emerald-50",
    heading: "text-emerald-900",
  },
  transcript: {
    icon: Mic,
    tone: "border-violet-300 bg-violet-50",
    heading: "text-violet-900",
  },
};

/**
 * Props:
 *   open — whether the dialog is open.
 *   suggestionId — the row to compare.
 *   onClose(applied) — dialog dismissed. `applied` is true if the
 *     clinician accepted the transcript value (caller should refresh).
 */
export default function SuggestionComparisonDialog({
  open, suggestionId, onClose,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState(null); // "accept" | "edit"
  const [editValue, setEditValue] = useState("");
  const [editReason, setEditReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);

  useEffect(() => {
    if (!open || !suggestionId) return;
    setLoading(true);
    setData(null);
    setAction(null);
    setEditValue("");
    setEditReason("");
    api.get(`/transcript-suggestions/${suggestionId}/comparison`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error("Could not load comparison", {
        description: e?.response?.data?.detail || e.message,
      }))
      .finally(() => setLoading(false));
  }, [open, suggestionId]);

  async function keepExisting() {
    // Nothing to persist against the assessment (existing already there),
    // but we mark the suggestion as REJECTED so the review queue clears.
    setSaving(true);
    try {
      await api.post(`/transcript-suggestions/${suggestionId}/reject`, {
        reject_reason: "Kept existing value after comparison.",
      });
      onClose(false);
    } catch (e) {
      toast.error("Could not update suggestion", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setSaving(false); }
  }

  async function acceptTranscript() {
    setSaving(true);
    try {
      await api.post(`/transcript-suggestions/${suggestionId}/accept`,
                       { overwrite: true });
      onClose(true);
    } catch (e) {
      toast.error("Could not overwrite", {
        description: e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message,
      });
    } finally { setSaving(false); }
  }

  async function editAndAccept() {
    if (!editValue || !editValue.trim()) {
      toast.error("Enter the final value first");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/transcript-suggestions/${suggestionId}/edit-accept`, {
        final_value: editValue,
        edit_reason: editReason,
        overwrite: true,
      });
      onClose(true);
    } catch (e) {
      toast.error("Could not save edited value", {
        description: e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message,
      });
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) onClose(false); }}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0"
        data-testid="suggestion-comparison-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-zinc-200 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <ScrollText className="h-4 w-4 text-zinc-600" />
            Compare sources before overwriting
          </DialogTitle>
          {data?.suggestion?.field_label && (
            <div className="text-sm text-zinc-600">
              Field: <b>{data.suggestion.field_label}</b>
              {data.suggestion.section_label && (
                <span className="text-zinc-500"> · {data.suggestion.section_label}</span>
              )}
            </div>
          )}
        </DialogHeader>

        <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0 space-y-3">
          {loading && <div className="text-sm text-zinc-500">Loading comparison…</div>}

          {data && (
            <>
              {data.onboarding.found && (
                <ComparisonBlock kind="onboarding" data={data.onboarding} />
              )}
              <ComparisonBlock kind="existing" data={data.existing} />
              <ComparisonBlock kind="transcript" data={data.transcript} />

              {/* Edit-and-accept panel — hidden until the clinician opts in */}
              {action === "edit" && (
                <div
                  className="mt-2 p-3 rounded border border-zinc-300 bg-zinc-50 space-y-2"
                  data-testid="suggestion-comparison-edit"
                >
                  <Label className="text-xs font-semibold">Edited final value</Label>
                  <Textarea
                    rows={2}
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    placeholder="Your corrected version of the answer"
                    data-testid="suggestion-comparison-edit-value"
                  />
                  <Label className="text-xs font-semibold">Why edit? (audit note)</Label>
                  <Textarea
                    rows={2}
                    value={editReason}
                    onChange={(e) => setEditReason(e.target.value)}
                    placeholder="e.g. Client clarified after the recorded statement"
                    data-testid="suggestion-comparison-edit-reason"
                  />
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="px-6 py-3 border-t border-zinc-200 bg-white shrink-0 flex-wrap gap-2">
          <div className="flex-1 text-2xs text-zinc-500 min-w-[220px]">
            <AlertTriangle className="h-3 w-3 inline mr-1 text-zinc-500" />
            The signed record identifies the practitioner as the responsible
            author (spec §10).
          </div>
          <Button
            variant="ghost"
            className="text-violet-700 hover:text-violet-800 hover:bg-violet-50"
            onClick={() => setEvidenceOpen(true)}
            disabled={saving || !data}
            data-testid="suggestion-comparison-open-evidence"
            title="Open evidence drawer — replay the audio moment this came from"
          >
            <Play className="h-3.5 w-3.5 mr-1" />
            Play evidence
          </Button>
          <Button
            variant="ghost"
            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
            onClick={() => setReportOpen(true)}
            disabled={saving || !data}
            data-testid="suggestion-comparison-report"
            title="Report transcription error — logs feedback for prompt tuning and rejects the suggestion"
          >
            <Flag className="h-3.5 w-3.5 mr-1" />
            Report error
          </Button>
          <Button
            variant="outline"
            onClick={() => onClose(false)}
            disabled={saving}
            data-testid="suggestion-comparison-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            className="border-emerald-400 text-emerald-800 hover:bg-emerald-50"
            onClick={keepExisting}
            disabled={saving || !data}
            data-testid="suggestion-comparison-keep-existing"
          >
            Keep existing
          </Button>
          {action !== "edit" ? (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setAction("edit");
                  setEditValue(String(data?.transcript?.value ?? ""));
                }}
                disabled={saving || !data}
                data-testid="suggestion-comparison-edit-open"
              >
                Edit & accept
              </Button>
              <Button
                className="bg-violet-700 hover:bg-violet-800 text-white"
                onClick={acceptTranscript}
                disabled={saving || !data}
                data-testid="suggestion-comparison-accept-transcript"
              >
                Accept transcript
              </Button>
            </>
          ) : (
            <Button
              className="bg-violet-700 hover:bg-violet-800 text-white"
              onClick={editAndAccept}
              disabled={saving || !editValue?.trim()}
              data-testid="suggestion-comparison-save-edit"
            >
              Save edited value
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
      <ReportTranscriptionErrorDialog
        open={reportOpen}
        suggestion={data?.suggestion || null}
        onClose={(reported) => {
          setReportOpen(false);
          if (reported) {
            // Suggestion was auto-rejected server-side; close the
            // comparison dialog and let the parent refresh its list.
            onClose(false);
          }
        }}
      />
      <EvidenceDrawer
        open={evidenceOpen}
        suggestion={data?.suggestion || null}
        onClose={() => setEvidenceOpen(false)}
      />
    </Dialog>
  );
}


function ComparisonBlock({ kind, data }) {
  const style = SOURCE_STYLES[kind];
  const Icon = style.icon;
  const value = data?.value;
  const empty = value === null || value === undefined || String(value).trim() === "";
  return (
    <div
      className={`rounded-lg border ${style.tone} p-3`}
      data-testid={`suggestion-comparison-${kind}`}
    >
      <div className={`text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 ${style.heading}`}>
        <Icon className="h-3.5 w-3.5" />
        {kind === "onboarding" && "Patient onboarding"}
        {kind === "existing" && "Existing assessment value"}
        {kind === "transcript" && "Transcript suggestion"}
      </div>
      <div className="text-2xs text-zinc-600 mt-0.5">{data?.source_label}</div>
      {empty ? (
        <div className="text-sm text-zinc-500 italic mt-2">— empty —</div>
      ) : (
        <div className="text-sm text-zinc-950 mt-2 whitespace-pre-wrap">
          {String(value)}
        </div>
      )}
      {kind === "transcript" && data?.evidence_text && (
        <div className="text-xs italic text-zinc-600 mt-2 border-l-2 border-violet-300 pl-2">
          &quot;{data.evidence_text}&quot;
        </div>
      )}
      {kind === "transcript" && data?.assertion && (
        <div className="flex flex-wrap gap-1 mt-2">
          {data.assertion.speaker && (
            <Chip label={`speaker: ${data.assertion.speaker}`} />
          )}
          {data.assertion.experiencer && data.assertion.experiencer !== "self" && (
            <Chip label={`about: ${data.assertion.experiencer.replace("_", " ")}`} tone="rose" />
          )}
          {data.assertion.laterality && data.assertion.laterality !== "unspecified" && (
            <Chip label={`side: ${data.assertion.laterality}`} />
          )}
          {data.assertion.units && (
            <Chip label={`units: ${data.assertion.units}`} />
          )}
          {data.assertion.certainty && data.assertion.certainty !== "stated" && (
            <Chip label={`certainty: ${data.assertion.certainty}`} />
          )}
          {data.assertion.temporality && data.assertion.temporality !== "current" && (
            <Chip label={`time: ${data.assertion.temporality}`} />
          )}
          {data.assertion.negation && (
            <Chip label="negated" tone="rose" />
          )}
          {typeof data?.confidence_score === "number" && (
            <Chip label={`${Math.round(data.confidence_score * 100)}% confident`} />
          )}
        </div>
      )}
    </div>
  );
}


function Chip({ label, tone = "zinc" }) {
  const cls = tone === "rose"
    ? "border-rose-300 bg-rose-100 text-rose-900"
    : "border-zinc-300 bg-white text-zinc-700";
  return (
    <span className={`text-2xs px-1.5 py-0.5 rounded border ${cls}`}>
      {label}
    </span>
  );
}
